// `POST /api/line-items/{id}/apply-to-similar {scope, category_id,
// is_business}` -- design §8.2 / D4, D-2 (founder report 2026-09-09):
// correcting a line item's category and extending that correction to
// similar transactions is one user action that writes a merchant `rules`
// row (D-1: keyed on the merchant's canonical `display_name`, e.g.
// "Amazon") and applies it SYNCHRONOUSLY -- every matching,
// non-user-labelled item is relabeled in the same request, no job, no LLM
// call needed (a merchant rule always fixes the leaf). `job_id` is always
// `null` on this response. The caller supplies the real counts from the
// PATCH response's `apply_to_similar` block (execution plan §S7 step 3) --
// this hook never estimates a count of its own.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

/** The PATCH response's `apply_to_similar` block -- real counts from a query, never estimated. */
export const applyToSimilarOptionsSchema = z.object({
  merchant_id: z.string(),
  merchant_display: z.string(),
  this_month: z.number(),
  all_time: z.number(),
});
export type ApplyToSimilarOptions = z.infer<typeof applyToSimilarOptionsSchema>;

// D-2 (founder revision §14): the removed "More options" this-month radio
// was the only caller of `'this_month'`, and it never actually narrowed the
// APPLICATION scope server-side (only a rule's future `effective_from`) --
// the backend's `ApplyToSimilarIn.scope` now rejects anything but
// `"all_time"` (422), so this stays a one-value literal, not a wider enum a
// caller could accidentally widen back into that mismatch.
export const applyToSimilarScopeSchema = z.literal('all_time');
export type ApplyToSimilarScope = z.infer<typeof applyToSimilarScopeSchema>;

export const applyToSimilarResultSchema = z.object({
  rule_id: z.string(),
  // D-2: apply-to-similar applies synchronously and rule-only -- no
  // recategorize job is enqueued, so `job_id` is always `null`.
  job_id: z.string().nullable(),
  batch_id: z.string(),
  applied_count: z.number(),
  skipped_user_count: z.number(),
  // Kept additively for compatibility with any caller still reading the
  // old field name -- always equal to `applied_count`.
  match_count: z.number(),
});
export type ApplyToSimilarResult = z.infer<typeof applyToSimilarResultSchema>;

export interface ApplyToSimilarInput {
  lineItemId: string;
  scope: ApplyToSimilarScope;
  categoryId: string;
  isBusiness?: boolean;
}

export function useApplyToSimilar() {
  const queryClient = useQueryClient();
  return useMutation<ApplyToSimilarResult, Error, ApplyToSimilarInput>({
    mutationFn: async ({ lineItemId, scope, categoryId, isBusiness }) => {
      const response = await client.post(`/api/line-items/${lineItemId}/apply-to-similar`, {
        scope,
        category_id: categoryId,
        is_business: isBusiness,
      });
      return parseEnvelope(applyToSimilarResultSchema, response.data).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
      // D-2: no job is enqueued here (this applies synchronously), but the
      // relabel still shifts Business and Categories view totals -- invalidate
      // both spend aggregation query prefixes so those views don't keep
      // showing pre-flip totals (ENG-R1-005).
      queryClient.invalidateQueries({ queryKey: ['spend-by-category'] });
      queryClient.invalidateQueries({ queryKey: ['spend-by-merchant'] });
    },
  });
}
